import { observable, runInAction } from "mobx";

import { compareDatesPredicate, DateTime } from "@bps/date-time";
import { sharePendingPromise } from "@libs/decorators/sharePendingPromise.ts";
import { EpisodeOfCareDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { Model } from "@libs/models/Model.ts";
import { IRootStore } from "@shared-types/root/root-store.interface.ts";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";

export class EpisodeOfCare extends Model<EpisodeOfCareDto> {
  constructor(
    private root: IRootStore,
    dto: EpisodeOfCareDto
  ) {
    super(dto);
  }

  public get patientId() {
    return this.dto.patientId;
  }

  public get changeLog() {
    return this.dto.changeLog;
  }

  public get primaryDiagnosis() {
    return (
      this.dto.diagnoses?.find(x => x.isPrimaryDiagnosis)?.diagnosisCode
        ?.originalText ?? "Undiagnosed"
    );
  }

  public get diagnosisSide() {
    return this.dto.diagnoses?.find(x => x.isPrimaryDiagnosis)?.diagnosisSide;
  }

  public get diagnoses() {
    return this.dto.diagnoses || [];
  }

  public get injuryDate() {
    return DateTime.fromISO(this.dto.injuryDate);
  }

  public get referralProvider() {
    return this.dto.referralProvider;
  }

  public get referralDate() {
    return DateTime.fromISO(this.dto.referralDate);
  }

  public get referralNumber() {
    return this.dto.referralNumber;
  }

  public get isReferral() {
    return this.dto.isReferral;
  }

  public get discharged() {
    return this.dto.discharged;
  }

  public get isPrivate() {
    return !this.claimEpisodeOfCare || !this.claimEpisodeOfCare?.isActive;
  }

  public get createdDate() {
    return DateTime.fromISO(this.dto.changeLog?.createdDate!);
  }

  public get sortDate() {
    return (
      this.mostRecentCalendarEvent?.startDateTime ||
      this.claim?.createdDate ||
      this.createdDate
    );
  }

  public get claimEpisodeOfCare() {
    return Array.from(this.root.acc.claimEpisodeOfCareMap.values()).find(
      ceoc => ceoc.episodeOfCareId === this.id
    );
  }

  public get inActiveClaim() {
    if (this.claimEpisodeOfCare?.claimId && !this.claimEpisodeOfCare.isActive) {
      return this.root.acc.claimsMap.get(this.claimEpisodeOfCare?.claimId);
    }
    return undefined;
  }

  public get claim() {
    if (
      !this.claimEpisodeOfCare?.claimId ||
      !this.claimEpisodeOfCare.isActive
    ) {
      return undefined;
    }
    return this.root.acc.claimsMap.get(this.claimEpisodeOfCare?.claimId);
  }

  public get patient() {
    return this.root.practice.contactsMap.get(this.patientId);
  }

  public get providerId() {
    return this.claim?.providerId;
  }

  public get sendAccForm() {
    return this.claim?.sendAccForm;
  }

  @sharePendingPromise()
  public async loadClaimEpisodeOfCare(options?: { includeInactive?: boolean }) {
    if (this.claim) return;

    return this.root.acc.getClaimEpisodesOfCare({
      episodesOfCare: [this.id],
      includeInactive: options?.includeInactive
    });
  }

  public async loadClaim() {
    if (this.claim) return;
    if (!this.root.core.hasPermissions(Permission.ClaimRead)) return;
    await this.loadClaimEpisodeOfCare();
    if (this.claimEpisodeOfCare?.claimId) {
      await this.root.acc.getClaim(this.claimEpisodeOfCare?.claimId);
    }
  }

  @observable calendarEvents: CalendarEvent[] | undefined;

  private get nextCalendarEvent() {
    return this.calendarEvents?.find(
      calendarEvent => !calendarEvent.startDateTime.isBeforeToday
    );
  }

  public get nextCalendarEventDate() {
    return this.nextCalendarEvent?.startTime;
  }

  public get nextCalendarEventProviderId() {
    return this.nextCalendarEvent?.userId;
  }

  public get initialCalendarEvent() {
    return this.calendarEvents?.[0];
  }

  public get mostRecentCalendarEvent() {
    return Array.from(this.calendarEvents ?? [])
      .reverse()
      .find(
        ({ startDateTime }) =>
          startDateTime.isToday || startDateTime.isBeforeToday
      );
  }

  public loadCalendarEvents = async () => {
    const result = await this.root.booking.getCalendarEvents({
      attendees: [this.patientId]
    });

    runInAction(() => {
      this.calendarEvents = result.results
        .filter(
          calendarEvent => calendarEvent.reason?.episodeOfCareId === this.id
        )
        .sort((a, b) =>
          compareDatesPredicate(a.startDateTime, b.startDateTime)
        );
    });
  };

  public async loadPatient() {
    await this.root.practice.getContact(this.patientId);
  }
}
